import dynamic from 'next/dynamic'

import { useUser } from '@hooks/user'
import type { SignupForm as ComponentType } from './SignupForm'
import type { AccountDetails as AccountComponentType } from './AccountDetails'
import { Button } from '@components/UI/Button'
import { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { FormValues } from '..'

const SignupForm = dynamic(
  async () => await import('./SignupForm').then(mod => mod.SignupForm as any)
) as typeof ComponentType

const AccountDetails = dynamic(
  async () =>
    await import('./AccountDetails').then(mod => mod.AccountDetails as any)
) as typeof AccountComponentType

interface UserAccountProps {
  openLoginModal: () => void
}

export function UserAccount(props: UserAccountProps) {
  const { isLoggedIn, logout } = useUser()
  const [showSignIn, setShowSignIn] = useState(false)
  const [mounted, setMounted] = useState(false)
  const { errors, submitCount, validateForm } = useFormikContext<FormValues>()

  useEffect(() => {
    if (!isLoggedIn && !showSignIn) {
      void validateForm()
    }
  }, [isLoggedIn, setShowSignIn])

  useEffect(() => {
    setMounted(true)
  }, [])

  function handleLogout() {
    setShowSignIn(false)
    void logout.mutate()
  }

  return (
    <div
      className="bg-white p-8 rounded shadow-sm"
      suppressHydrationWarning={true}
      id="signup"
    >
      <h2 className="text-2xl font-medium mb-8" suppressHydrationWarning={true}>
        Account details
      </h2>
      <p className="mb-4">
        Your Home Repair Estimate will be ready in 24 hours or less. When it's
        ready, we'll send you an email with a link.{' '}
        <span className="italic">
          We will never share your contact information with anyone else.
        </span>
      </p>

      {mounted && !showSignIn && !isLoggedIn && (
        <div>
          {submitCount > 0 && errors.signup && (
            <p className="w-full text-red-400 text-sm text-center rounded mb-4">
              Please log in to an existing Majordomo account or create a new one
            </p>
          )}

          <div className="grid sm:grid-cols-2 grid-cols-1 sm:gap-4 sm:divide-x">
            <div className="flex flex-col items-center justify-center sm:p-8 p-4 sm:order-1 order-2">
              <Button
                variant="brand"
                onClick={e => {
                  e.preventDefault()
                  setShowSignIn(true)
                }}
              >
                Create a Home Inspection Account
              </Button>
            </div>

            <div className="flex items-center justify-center sm:p-8 p-4 sm:order-2 order-1">
              <Button
                variant="brand"
                onClick={e => {
                  e.preventDefault()
                  props.openLoginModal()
                }}
              >
                Log in to an existing account
              </Button>
            </div>
          </div>
        </div>
      )}

      {mounted && isLoggedIn && <AccountDetails handleLogout={handleLogout} />}
      {mounted && showSignIn && !isLoggedIn && (
        <p className="mb-4 w-full">
          Already have an account?{' '}
          <a
            className="text-brand hover:underline cursor-pointer"
            onClick={props.openLoginModal}
          >
            Log in
          </a>
        </p>
      )}
      {mounted && showSignIn && !isLoggedIn && (
        <SignupForm openLoginModal={props.openLoginModal} />
      )}

      <p className="text-xs text-center mt-4">
        Your Home Repair Estimate login credentials are separate from your
        Thumbtack credentials.
      </p>
    </div>
  )
}
